import { useQuery } from '@tanstack/react-query'
import { Camera, X } from 'lucide-react'
import {
  Bidi, Button, Card, CardBody, CardHeader, ErrorState, LoadingState,
  NoPermissionState, useLocale,
} from '@hk/ui'
import {
  ApiError, fetchTaskDetail, formatCount, formatCredits, formatDateTime, formatMinutes,
  nameIn, translateError, type TaskAnswerLine, type TaskHolder,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'

/** The same `.replace` every screen in this folder does. */
const fill = (template: string, values: Record<string, string>) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, value), template)

/* ============================================================================
   W-31 — ONE PIECE OF WORK, AND EVERYTHING THAT HAPPENED TO IT.  Sprint 80.

   ── WHY IT WAS HELD BACK FOR EIGHTY SPRINTS ────────────────────────────────────
   A NUMBERING dispute: the design packs use W-31 for task detail and the Screen Inventory
   used it for area detail. That was a reason to wait while the packs were being settled
   and stopped being one once everything around it was built. **The collision is a question
   about a name, not about a screen.**

   ── WHAT IT IS FOR ─────────────────────────────────────────────────────────────
   Every other screen shows a task as a card in a list — a room number, a state, a credit
   figure. That is right for a board and useless for the one moment somebody is asking
   about a particular task: *"this room was rejected twice — what actually happened?"*

   ── IT COMPUTES NOTHING ────────────────────────────────────────────────────────
   No "time taken", no "was this slow". A derived figure here is a second opinion about
   what the reports already say, and the two disagree the first time a rule changes. The
   three timestamps are drawn and the reader draws their own conclusion — with one sentence
   of help, because **the first start survives a rejection** and a reader who does not know
   that will conclude an attendant took six hours over one room.
   ========================================================================= */

export function TaskDetail({ taskId, onClose }: { taskId: string; onClose: () => void }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const detail = useQuery({
    queryKey: ['task-detail', taskId],
    queryFn: ({ signal }) => fetchTaskDetail(taskId, { signal }),
    retry: false,
  })

  if (detail.isLoading && !detail.error) return <LoadingState rows={5} />

  if (detail.error && !detail.data) {
    if (detail.error instanceof ApiError && detail.error.code === 'permission.denied')
      return <NoPermissionState />
    const translated = translateError(detail.error, locale)
    return (
      <ErrorState
        body={translated.message}
        onRetry={detail.refetch}
        {...(translated.correlationId ? { correlationId: translated.correlationId } : {})}
      />
    )
  }

  const view = detail.data!
  const where = view.roomNumber ?? nameIn(locale, view.areaName ?? '', view.areaNameAr)

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-medium">
          {fill(t.tdTitle, { where })}
        </h2>
        <Button type="button" size="sm" variant="ghost" onClick={onClose}>
          <X className="size-3.5" aria-hidden />
          {t.close}
        </Button>
      </div>

      <Card>
        <CardHeader title={t.tdWhat} />
        <CardBody className="space-y-1.5 text-sm">
          <Line label={t.tdKind} value={view.taskTypeCode} ltr />
          <Line label={t.tdState} value={t.tdStates[view.state] ?? view.state} />
          <Line
            label={t.tdWorth}
            value={fill(t.tdCredits, { credits: formatCredits(view.credits, locale) })}
          />
          <Line
            label={t.tdEstimate}
            value={formatMinutes(view.estimatedMinutes, locale)}
          />
          {/* **WHAT RAISED IT.** A clean the sweep raised and a clean a supervisor asked
              for are different facts about a morning, and only one of them is a decision
              somebody took. */}
          <Line
            label={t.tdRaisedBy}
            value={view.generatedByTrigger ? t.tdBySweep : t.tdByAPerson}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={t.tdWhen} />
        <CardBody className="space-y-1.5 text-sm">
          <Line
            label={t.tdStarted}
            value={view.firstStartedAtUtc
              ? formatDateTime(new Date(view.firstStartedAtUtc), locale)
              : t.tdNotYet}
          />
          <Line
            label={t.tdSubmitted}
            value={view.submittedAtUtc
              ? formatDateTime(new Date(view.submittedAtUtc), locale)
              : t.tdNotYet}
          />
          <Line
            label={t.tdCompleted}
            value={view.completedAtUtc
              ? formatDateTime(new Date(view.completedAtUtc), locale)
              : t.tdNotYet}
          />

          {/* **SAID ONLY WHERE IT CHANGES THE READING.** A room that was handed back and
              cleaned again has a first start that is older than its last clean, and a
              reader who does not know that reaches a conclusion about a person. */}
          {view.held.length > 1 && (
            <p className="pt-1 text-[12px] text-muted-foreground">{t.tdFirstStartNote}</p>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={t.tdWhoHeld} />
        <CardBody className="space-y-2">
          {view.held.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t.tdNobodyHeld}</p>
          ) : (
            view.held.map((holder) => <Holder key={holder.assignmentId} holder={holder} />)
          )}
        </CardBody>
      </Card>

      {view.checklist.length > 0 && (
        <Card>
          <CardHeader title={t.tdChecklist} />
          <CardBody className="space-y-2">
            {view.checklist.map((line) => (
              <Answer key={line.checklistItemId} line={line} />
            ))}
          </CardBody>
        </Card>
      )}

      {view.linen.length > 0 && (
        <Card>
          <CardHeader title={t.tdLinen} />
          <CardBody>
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-border text-[11.5px] text-muted-foreground">
                  <th className="px-2 py-1.5 text-start font-medium">{t.tdItem}</th>
                  <th className="px-2 py-1.5 text-end font-medium">{t.tdStandard}</th>
                  <th className="px-2 py-1.5 text-end font-medium">{t.tdUsed}</th>
                </tr>
              </thead>
              <tbody>
                {view.linen.map((line) => (
                  <tr key={line.stockItemId} className="border-b border-border/60 last:border-0">
                    <td className="px-2 py-1.5">
                      <Bidi>{nameIn(locale, line.name, line.nameAr)}</Bidi>
                    </td>
                    <td className="px-2 py-1.5 text-end tnum text-muted-foreground">
                      {formatCount(line.standard, locale)}
                    </td>
                    <td className="px-2 py-1.5 text-end tnum">
                      {formatCount(line.effective, locale)}
                      {/* **A VARIANCE IS NOTED, NOT ARGUED WITH.** A room that took three
                          towels took three towels — M-21's own rule, and drawing this in
                          red would make it an accusation. */}
                      {line.variance !== 0 && (
                        <span className="ms-1 text-[11px] text-muted-foreground">
                          {line.variance > 0 ? '+' : ''}{formatCount(line.variance, locale)}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      )}
    </div>
  )
}

function Holder({ holder }: { holder: TaskHolder }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  return (
    <div className="border-b border-border/60 pb-2 last:border-0 last:pb-0">
      <p className="text-sm font-medium">
        <Bidi>{nameIn(locale, holder.who, holder.whoAr)}</Bidi>
      </p>
      <p className="text-[12px] text-muted-foreground">
        {formatDateTime(new Date(holder.assignedAtUtc), locale)}
        {holder.unassignedAtUtc
          ? ` — ${formatDateTime(new Date(holder.unassignedAtUtc), locale)}`
          : ` · ${t.tdStillHolding}`}
        {holder.givenBy && <>{' · '}{fill(t.tdGivenBy, { who: holder.givenBy })}</>}
      </p>
      {/* **THE REASON IS DRAWN AS IT WAS TYPED.** Somebody wrote it about somebody. */}
      {holder.reason && (
        <p className="mt-0.5 text-[12px]"><Bidi>{holder.reason}</Bidi></p>
      )}
    </div>
  )
}

function Answer({ line }: { line: TaskAnswerLine }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  return (
    <div className="flex items-start justify-between gap-2 border-b border-border/60 pb-1.5 last:border-0 last:pb-0">
      <span className="min-w-0 flex-1 text-sm">
        <Bidi>{nameIn(locale, line.text, line.textAr)}</Bidi>
        {line.note && (
          <span className="mt-0.5 block text-[12px] text-muted-foreground">
            <Bidi>{line.note}</Bidi>
          </span>
        )}
      </span>

      <span className="flex shrink-0 items-center gap-2 text-[12px]">
        {line.photographs > 0 && (
          <span className="flex items-center gap-1 text-muted-foreground">
            <Camera className="size-3.5" aria-hidden />
            <span className="tnum">{formatCount(line.photographs, locale)}</span>
          </span>
        )}
        {/* **UNANSWERED IS ITS OWN WORD.** Blank would read as "no", which is the answer
            an attendant did not give. */}
        <span className="font-medium">
          {line.answer === null
            ? t.tdUnanswered
            : t.tdAnswers[line.answer] ?? line.answer}
        </span>
      </span>
    </div>
  )
}

function Line({ label, value, ltr }: { label: string; value: string; ltr?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="tnum font-medium" {...(ltr ? { dir: 'ltr' } : {})}>{value}</span>
    </div>
  )
}
